import { useI18n, type MessageKey } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import {
  Badge,
  Button,
  EmptyState,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@ecom/ui-kit';
import { type ReactElement } from 'react';
import { Link } from 'react-router-dom';

import { useMySecurityQuery, useRevokeOtherSessionsMutation } from '../api';

import { TableSkeleton } from '@/components/ui/skeletons/TableSkeleton';
import { useToast } from '@/components/ui/toast/ToastProvider';

const KNOWN_EVENTS = new Set([
  'auth.login_succeeded',
  'auth.login_failed',
  'auth.account_locked',
  'auth.password_changed',
  'auth.password_change_failed',
  'auth.password_reset_completed',
  'auth.sessions_revoked',
  'auth.reauth_failed',
  'auth.temporary_password_expired',
]);

function eventLabelKey(action: string): MessageKey {
  return KNOWN_EVENTS.has(action)
    ? (`account.event.${action}` as MessageKey)
    : 'account.event.unknown';
}

/**
 * "My account" (lot B3): the signed-in person's own security history - the last time they signed in before now, their sessions (with the
 * address and the browser, kept 90 days), and what happened to the account (a sign-in, a failed attempt, a lock, a password change).
 * Everything comes from `GET /auth/me/security`, which reads the account named by the TOKEN: this screen cannot show anybody else's.
 * "Sign out everywhere else" ends every other session and keeps this one, for the day a device may not be theirs any more.
 */
export function MyAccountPage(): ReactElement {
  const { t } = useI18n();
  const toast = useToast();
  const query = useMySecurityQuery();
  const revoke = useRevokeOtherSessionsMutation();

  async function onRevokeOthers(): Promise<void> {
    try {
      const result = await revoke.mutateAsync();
      toast.success(t('account.sessions.revokedToast', { count: result.sessionsEnded }));
    } catch (error) {
      toast.error(t(resolveErrorMessageKey(error)));
    }
  }

  const data = query.data;
  const previous = data?.events.filter((event) => event.action === 'auth.login_succeeded')[1];
  const hasOtherActive =
    data?.sessions.some((session) => session.isActive && !session.isCurrent) ?? false;

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-ink">{t('account.title')}</h1>
          <p className="text-sm text-inkFaint">{t('account.subtitle')}</p>
        </div>
        <Link to="/change-password" className="text-sm font-medium text-accent underline">
          {t('account.changePassword')}
        </Link>
      </div>

      {query.isPending ? (
        <TableSkeleton columns={5} />
      ) : query.isError || data === undefined ? (
        <EmptyState title={t('account.loadError')} />
      ) : (
        <>
          <section className="rounded-xl border border-line bg-surface p-4">
            <h2 className="font-display text-base font-semibold text-ink">
              {t('account.lastSignIn.title')}
            </h2>
            <p className="text-sm text-inkSoft">
              {previous === undefined
                ? t('account.lastSignIn.none')
                : previous.ipAddress === null
                  ? t('account.lastSignIn.valueNoIp', {
                      date: new Date(previous.createdAt).toLocaleString('fr-TN'),
                    })
                  : t('account.lastSignIn.value', {
                      date: new Date(previous.createdAt).toLocaleString('fr-TN'),
                      ip: previous.ipAddress,
                    })}
            </p>
          </section>

          <section className="flex flex-col gap-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="font-display text-base font-semibold text-ink">
                  {t('account.sessions.title')}
                </h2>
                <p className="text-xs text-inkFaint">{t('account.sessions.retention')}</p>
              </div>
              {hasOtherActive ? (
                <div className="flex max-w-sm flex-col items-end gap-1">
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={revoke.isPending}
                    onClick={() => {
                      void onRevokeOthers();
                    }}
                  >
                    {t('account.sessions.revokeOthers')}
                  </Button>
                  <p className="text-end text-xs text-inkFaint">
                    {t('account.sessions.revokeOthersHint')}
                  </p>
                </div>
              ) : null}
            </div>

            {data.sessions.length === 0 ? (
              <EmptyState title={t('account.sessions.empty')} />
            ) : (
              <Table caption={t('account.sessions.caption')}>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>{t('account.sessions.colOpened')}</TableHeaderCell>
                    <TableHeaderCell>{t('account.sessions.colLastUsed')}</TableHeaderCell>
                    <TableHeaderCell>{t('account.sessions.colIp')}</TableHeaderCell>
                    <TableHeaderCell>{t('account.sessions.colBrowser')}</TableHeaderCell>
                    <TableHeaderCell>{t('account.sessions.colState')}</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.sessions.map((session) => (
                    <TableRow key={session.sessionId}>
                      <TableCell>
                        <span className="text-xs text-inkFaint">
                          {new Date(session.createdAt).toLocaleString('fr-TN')}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs text-inkFaint">
                          {new Date(session.lastUsedAt).toLocaleString('fr-TN')}
                        </span>
                      </TableCell>
                      <TableCell>
                        {session.ipAddress === null ? (
                          <span className="text-xs text-inkFaint">
                            {t('account.sessions.erased')}
                          </span>
                        ) : (
                          <span className="font-mono text-xs text-ink">{session.ipAddress}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="break-all text-xs text-inkSoft">
                          {session.userAgent ?? '—'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col items-start gap-1">
                          <Badge tone={session.isActive ? 'good' : 'neutral'}>
                            {t(
                              session.isActive
                                ? 'account.sessions.active'
                                : 'account.sessions.ended',
                            )}
                          </Badge>
                          {session.isCurrent ? (
                            <Badge tone="info">{t('account.sessions.current')}</Badge>
                          ) : null}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </section>

          <section className="flex flex-col gap-3">
            <h2 className="font-display text-base font-semibold text-ink">
              {t('account.events.title')}
            </h2>
            {data.events.length === 0 ? (
              <EmptyState title={t('account.events.empty')} />
            ) : (
              <Table caption={t('account.events.caption')}>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>{t('account.events.colWhen')}</TableHeaderCell>
                    <TableHeaderCell>{t('account.events.colEvent')}</TableHeaderCell>
                    <TableHeaderCell>{t('account.events.colIp')}</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.events.map((event, index) => (
                    <TableRow key={`${event.createdAt}-${String(index)}`}>
                      <TableCell>
                        <span className="text-xs text-inkFaint">
                          {new Date(event.createdAt).toLocaleString('fr-TN')}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-ink">{t(eventLabelKey(event.action))}</span>
                      </TableCell>
                      <TableCell>
                        <span className="font-mono text-xs text-inkFaint">
                          {event.ipAddress ?? '—'}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </section>
        </>
      )}
    </div>
  );
}
